import {
  Box,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { RailRouteMap } from "./RailRouteMap";
import { useStations, useRatesToMills, useTruckRoutes } from "./useRailRates";
import { useRailRoutes } from "./useRailRoutes";
import { generatedApi, type RailStation } from "src/store/api/generatedApi";
import { RatesTable } from "./RatesTable";
import { MillsLegend } from "./MillsLegend";
import { RouteTypeLegend } from "./RouteTypeLegend";

export const RailRouteMapPage = () => {
  const { t } = useTranslation();
  const { tenantGroup } = useParams<{ tenantGroup: string }>();
  const [sendNotification] =
    generatedApi.useSendLogisticsRoutingNotificationMutation();

  useEffect(() => {
    void sendNotification({ tenantGroupName: tenantGroup });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only notification; sendNotification and tenantGroup are stable for the page lifetime
  }, []);

  const [selectedStation, setSelectedStation] = useState<RailStation | null>(
    null
  );

  const {
    stations,
    mills,
    isFetching: stationsFetching,
    error: stationsError,
  } = useStations(tenantGroup);

  const {
    ratesData,
    isFetching: ratesFetching,
    error: ratesError,
  } = useRatesToMills(
    tenantGroup,
    selectedStation?.station_name ?? null,
    selectedStation?.station_state ?? null
  );

  const {
    truckRoutes,
    isLoading: truckLoading,
    error: truckError,
  } = useTruckRoutes(
    selectedStation?.station_name ?? null,
    selectedStation?.station_state ?? null,
    mills
  );

  const millsWithRailRates = useMemo(() => {
    if (!ratesData) return [];
    const ratedTenants = new Set(
      ratesData.rates
        .filter(
          (r) =>
            r.rate_per_ton_private_car_usd != null ||
            r.rate_per_ton_railroad_car_usd != null ||
            r.rate_per_ton_all_car_usd != null
        )
        .map((r) => r.tenant_name)
    );
    return mills.filter((m) => ratedTenants.has(m.tenant_name));
  }, [ratesData, mills]);

  const {
    originCoordinates,
    routes,
    isLoading: routesLoading,
    geocodeError,
  } = useRailRoutes(
    selectedStation?.station_name ?? null,
    selectedStation?.station_state ?? null,
    millsWithRailRates
  );

  const truckAvailable = !geocodeError;
  const error =
    stationsError ?? ratesError ?? (truckAvailable ? truckError : null);
  const allRoutesReady =
    selectedStation !== null &&
    !ratesFetching &&
    !routesLoading &&
    !truckLoading;
  const isLoadingAny = selectedStation !== null && !allRoutesReady;

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4" component="h1" fontWeight="bold" sx={{ pb: 2 }}>
        {t("logistics")}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Paper sx={{ width: 400, p: 2, overflow: "auto" }}>
          <Stack spacing={2}>
            <Typography variant="h6">{t("logisticsRateCalculator")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("logisticsSelectStation")}
            </Typography>

            <Autocomplete
              options={stations}
              loading={stationsFetching}
              value={selectedStation}
              onChange={(_, value) => {
                setSelectedStation(value);
              }}
              getOptionLabel={(option) =>
                `${option.station_name}, ${option.station_state}`
              }
              isOptionEqualToValue={(option, value) =>
                option.station_name === value.station_name &&
                option.station_state === value.station_state
              }
              groupBy={(option) => option.station_state}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("logisticsOriginStation")}
                  placeholder={t("logisticsSearchStations")}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {stationsFetching ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              renderOption={({ key, ...props }, option) => {
                return (
                  <li key={key} {...props}>
                    {option.station_name}, {option.station_state}
                  </li>
                );
              }}
            />

            {isLoadingAny ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <CircularProgress size={24} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {t("logisticsLoadingData")}
                </Typography>
              </Box>
            ) : null}

            {error ? <Alert severity="error">{t(error)}</Alert> : null}
            {geocodeError ? (
              <Alert severity="warning">{t(geocodeError)}</Alert>
            ) : null}

            {ratesData ? (
              <RatesTable
                ratesData={ratesData}
                truckRoutes={truckAvailable ? truckRoutes : {}}
                mills={mills}
              />
            ) : null}

            {mills.length > 0 && (
              <>
                <MillsLegend mills={mills} />
                <RouteTypeLegend />
              </>
            )}
          </Stack>
        </Paper>

        <Box sx={{ flex: 1 }}>
          <RailRouteMap
            mills={mills}
            routes={allRoutesReady ? routes : {}}
            originCoordinates={allRoutesReady ? originCoordinates : null}
            truckRoutes={allRoutesReady && truckAvailable ? truckRoutes : {}}
          />
        </Box>
      </Stack>
    </Box>
  );
};
